import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CreditCard,
  Plus,
  Trash2,
  WalletCards,
  Printer,
} from 'lucide-react';

import { registrosApi } from '../../../api/registrosApi';
import PrecuentaImprimible from './PrecuentaImprimible';

const money = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const redondear2 = (value) =>
  Number(Number(value || 0).toFixed(2));

const nuevoPago = (medioPagoId = '') => ({
  key: `${Date.now()}-${Math.random()}`,
  medio_pago_id: medioPagoId,
  monto: '',
});

export default function CuentaMesaPanel({
  cuenta,
  cargando = false,
  procesando = false,
  error = '',
  onCobrar,
  onAnularPago,
  mesa,
  sesion,
  pedidos = [],
}) {
  const [mediosPago, setMediosPago] = useState([]);
  const [cargandoMedios, setCargandoMedios] =
    useState(false);
  const [errorMedios, setErrorMedios] = useState('');
  const [pagos, setPagos] = useState([]);
  const primerMontoRef = useRef(null);
  const cantidadPagosPrevia = useRef(0);
  const [sugerirPropina, setSugerirPropina] =
    useState(false);

  const consumoTotal = redondear2(
    cuenta?.resumen?.consumo_total ?? cuenta?.total
  );

  const pagado = redondear2(
    cuenta?.resumen?.pagado
  );

  const propinaPersistida = redondear2(
    cuenta?.resumen?.propina_monto ??
      cuenta?.propina_monto
  );

  const propinaEditable =
    cuenta?.resumen?.propina_editable !== false;

  const propinaSugerida = redondear2(
    consumoTotal * 0.10
  );

  const propinaAplicada = redondear2(
    propinaEditable
      ? sugerirPropina
        ? propinaSugerida
        : 0
      : propinaPersistida
  );

  const total = redondear2(
    consumoTotal + propinaAplicada
  );

  const saldo = redondear2(
    Math.max(0, total - pagado)
  );

  useEffect(() => {
    setSugerirPropina(
      redondear2(
        cuenta?.resumen?.propina_monto ??
          cuenta?.propina_monto
      ) > 0
    );
  }, [cuenta?.id, cuenta?.resumen?.propina_monto, cuenta?.propina_monto]);

  useEffect(() => {
    let activo = true;

    const cargarMedios = async () => {
      try {
        setCargandoMedios(true);
        setErrorMedios('');

        const response =
          await registrosApi.getMediosDePago();

        if (!activo) return;

        const data = Array.isArray(response.data)
          ? response.data
          : [];

        setMediosPago(data);

        if (data.length) {
          setPagos([
            nuevoPago(data[0].id),
          ]);
        }
      } catch (err) {
        console.error(
          'Error cargando medios de pago Restaurante:',
          err
        );

        if (activo) {
          setMediosPago([]);
          setErrorMedios(
            'No se pudieron cargar los medios de pago.'
          );
        }
      } finally {
        if (activo) {
          setCargandoMedios(false);
        }
      }
    };

    cargarMedios();

    return () => {
      activo = false;
    };
  }, [cuenta?.id]);

  useEffect(() => {
    if (saldo <= 0) return;

    setPagos((actuales) => {
      if (actuales.length !== 1) {
        return actuales;
      }

      const actual = actuales[0];

      if (Number(actual.monto || 0) === saldo) {
        return actuales;
      }

      return [
        {
          ...actual,
          monto: String(saldo),
        },
      ];
    });
  }, [saldo, mediosPago.length]);

  const totalIngresado = useMemo(
    () =>
      redondear2(
        pagos.reduce(
          (acumulado, pago) =>
            acumulado +
            Number(pago.monto || 0),
          0
        )
      ),
    [pagos]
  );

  const restante = redondear2(
    Math.max(0, saldo - totalIngresado)
  );

  // Con dos medios de pago, los montos suman el saldo: al escribir uno,
  // el otro se completa solo con lo que resta.
  const complemento = (valor) => {
    const numero = redondear2(valor);

    return numero > 0 && numero < saldo
      ? String(redondear2(saldo - numero))
      : '';
  };

  const actualizarPago = (key, campo, valor) => {
    setPagos((actuales) =>
      actuales.map((pago) => {
        if (pago.key === key) {
          return { ...pago, [campo]: valor };
        }

        if (campo === 'monto' && actuales.length === 2) {
          return { ...pago, monto: complemento(valor) };
        }

        return pago;
      })
    );
  };

  // Al dividir, el cursor va al primer monto para escribirlo directo.
  useEffect(() => {
    if (cantidadPagosPrevia.current === 1 && pagos.length === 2) {
      primerMontoRef.current?.focus();
      primerMontoRef.current?.select();
    }

    cantidadPagosPrevia.current = pagos.length;
  }, [pagos.length]);

  const agregarPago = () => {
    const medioInicial = mediosPago[0]?.id || '';

    setPagos((actuales) => [
      ...actuales,
      {
        ...nuevoPago(medioInicial),
        monto:
          restante > 0
            ? String(restante)
            : '',
      },
    ]);
  };

  const quitarPago = (key) => {
    setPagos((actuales) => {
      const restantes = actuales.filter((pago) => pago.key !== key);

      // Si queda un solo medio, vuelve a cubrir todo el saldo.
      return restantes.length === 1
        ? [{ ...restantes[0], monto: String(saldo) }]
        : restantes;
    });
  };

  const cobrar = () => {
    if (
      procesando ||
      saldo <= 0 ||
      !pagos.length
    ) {
      return;
    }

    const normalizados = pagos.map((pago) => ({
      medio_pago_id: Number(pago.medio_pago_id),
      monto: redondear2(pago.monto),
    }));

    const invalido = normalizados.some(
      (pago) =>
        !Number.isInteger(pago.medio_pago_id) ||
        pago.medio_pago_id < 1 ||
        !Number.isFinite(pago.monto) ||
        pago.monto <= 0
    );

    if (invalido) {
      return;
    }

    if (totalIngresado > saldo + 0.01) {
      return;
    }

    onCobrar?.(
      normalizados,
      propinaEditable
        ? sugerirPropina
        : propinaPersistida > 0
    );
  };

  if (cargando) {
    return (
      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-500">
          Cargando cuenta...
        </p>
      </div>
    );
  }

  if (!cuenta) return null;

  const imprimirPrecuenta = () => {
    const items = pedidos.flatMap(
      (pedido) => pedido.items || []
    );

    const ventana = window.open(
      '',
      'precuenta',
      'width=420,height=700'
    );

    if (!ventana) return;

    const escapar = (valor = '') =>
      String(valor)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const logo =
      process.env.REACT_APP_PDF_LOGO ||
      '/logobigua.PNG';

    const filas = items
      .map(
        (item) => `
          <div class="item">
            <div class="linea">
              <span>${escapar(item.cantidad)} × ${escapar(
                item.producto_nombre
              )}</span>
              <strong>${money(item.subtotal)}</strong>
            </div>
            <div class="unitario">
              ${money(item.precio_unitario)} c/u
            </div>
          </div>
        `
      )
      .join('');

    ventana.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Precuenta</title>
          <style>
            @page { size: 80mm auto; margin: 4mm; }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              font-family: Arial, sans-serif;
              color: #000;
              font-size: 12px;
            }
            .ticket {
              width: 72mm;
              margin: 0 auto;
            }
            .logo {
              display: block;
              max-width: 48mm;
              max-height: 22mm;
              margin: 0 auto 8px;
              object-fit: contain;
            }
            h1 {
              margin: 0;
              text-align: center;
              font-size: 17px;
            }
            .subtitulo {
              text-align: center;
              margin: 3px 0 0;
            }
            .separador {
              border-top: 1px dashed #000;
              margin: 10px 0;
            }
            .datos div {
              margin: 3px 0;
            }
            .item {
              margin: 7px 0;
            }
            .linea {
              display: flex;
              justify-content: space-between;
              gap: 8px;
            }
            .unitario {
              font-size: 10px;
              margin-top: 2px;
            }
            .total-linea {
              display: flex;
              justify-content: space-between;
              margin: 4px 0;
            }
            .total-final {
              font-size: 16px;
              font-weight: bold;
              margin-top: 8px;
            }
            .pie {
              text-align: center;
              font-size: 10px;
              margin-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="ticket">
            <img
              class="logo"
              src="${escapar(logo)}"
              alt="Logo"
            >

            <h1>PRECUENTA</h1>
            <div class="subtitulo">
              Comprobante de consumo
            </div>

            <div class="separador"></div>

            <div class="datos">
              <div><strong>Mesa:</strong> ${escapar(
                mesa?.alias || mesa?.nombre || '-'
              )}</div>
              <div><strong>Comensales:</strong> ${escapar(
                sesion?.comensales || 1
              )}</div>
              <div><strong>Fecha:</strong> ${escapar(
                new Date().toLocaleString('es-AR')
              )}</div>
            </div>

            <div class="separador"></div>

            ${filas || '<div>Sin consumos.</div>'}

            <div class="separador"></div>

            <div class="total-linea">
              <span>Consumo</span>
              <strong>${money(consumoTotal)}</strong>
            </div>

            ${
              propinaAplicada > 0
                ? `
                  <div class="total-linea">
                    <span>Propina sugerida 10%</span>
                    <strong>${money(propinaAplicada)}</strong>
                  </div>
                `
                : ''
            }

            <div class="total-linea total-final">
              <span>TOTAL</span>
              <span>${money(total)}</span>
            </div>

            <div class="separador"></div>

            <div class="pie">
              Precuenta informativa.<br>
              No registra ningún pago.<br><br>
              <strong>Gracias por compartir este momento con nosotros.</strong>
            </div>
          </div>

          <script>
            window.onload = function () {
              setTimeout(function () {
                window.print();
              }, 250);
            };
          </script>
        </body>
      </html>
    `);

    ventana.document.close();
  };

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm">
      <PrecuentaImprimible
        mesa={mesa}
        sesion={sesion}
        pedidos={pedidos}
        consumoTotal={consumoTotal}
        propina={propinaAplicada}
        total={total}
      />

      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-verde-esmeralda">
          <WalletCards size={21} />
        </div>

        <div>
          <h2 className="font-extrabold text-azul-oscuro">
            Cuenta
          </h2>

          <p className="text-xs text-gray-500">
            {cuenta.nombre || 'Cuenta principal'}
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-[11px] font-bold uppercase text-gray-400">
            Consumo
          </p>

          <strong className="mt-1 block text-azul-oscuro">
            {money(consumoTotal)}
          </strong>
        </div>

        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-[11px] font-bold uppercase text-gray-400">
            Propina
          </p>

          <strong className="mt-1 block text-azul-oscuro">
            {money(propinaAplicada)}
          </strong>
        </div>

        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-[11px] font-bold uppercase text-gray-400">
            Pagado
          </p>

          <strong className="mt-1 block text-azul-oscuro">
            {money(pagado)}
          </strong>
        </div>

        <div className="rounded-xl bg-emerald-50 p-3">
          <p className="text-[11px] font-bold uppercase text-emerald-600">
            Saldo
          </p>

          <strong className="mt-1 block text-lg text-emerald-700">
            {money(saldo)}
          </strong>
        </div>
      </div>

      {saldo > 0 && propinaEditable && (
        <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-3">
          <div>
            <p className="text-sm font-extrabold text-azul-oscuro">
              Sugerir propina
            </p>

            <p className="mt-0.5 text-xs text-gray-500">
              10% del consumo · {money(propinaSugerida)}
            </p>
          </div>

          <input
            type="checkbox"
            checked={sugerirPropina}
            onChange={(event) =>
              setSugerirPropina(event.target.checked)
            }
            disabled={procesando}
            className="h-5 w-5 accent-emerald-600"
          />
        </label>
      )}

      {propinaAplicada > 0 && (
        <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm">
          <span className="font-semibold text-gray-500">
            Total con propina
          </span>

          <strong className="text-azul-oscuro">
            {money(total)}
          </strong>
        </div>
      )}

      {!!cuenta.pagos?.length && (
        <div className="mt-5">
          <p className="text-xs font-extrabold uppercase tracking-wide text-gray-400">
            Pagos realizados
          </p>

          <div className="mt-2 space-y-2">
            {cuenta.pagos
              .filter(
                (pago) =>
                  pago.estado === 'APLICADO'
              )
              .map((pago) => (
                <div
                  key={pago.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2 text-sm"
                >
                  <span className="font-semibold text-gray-600">
                    {pago.medioPago?.nombre ||
                      'Medio de pago'}
                  </span>

                  <span className="flex items-center gap-3">
                    <strong className="text-azul-oscuro">
                      {money(pago.monto)}
                    </strong>

                    {onAnularPago && (
                      <button
                        type="button"
                        onClick={() => onAnularPago(pago)}
                        disabled={procesando}
                        aria-label={`Anular el pago de ${money(
                          pago.monto
                        )}`}
                        className="text-xs font-bold text-red-500 underline-offset-2 transition hover:underline disabled:opacity-40"
                      >
                        Anular
                      </button>
                    )}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {saldo > 0 && (
        <>
          <div className="my-5 border-t border-gray-100" />

          <div className="flex items-center justify-between">
            <p className="text-sm font-extrabold text-azul-oscuro">
              Cobrar
            </p>

            <button
              type="button"
              onClick={agregarPago}
              disabled={
                cargandoMedios ||
                procesando ||
                !mediosPago.length
              }
              className="inline-flex items-center gap-1 text-xs font-bold text-verde-esmeralda disabled:opacity-40"
            >
              <Plus size={15} />
              Dividir pago
            </button>
          </div>

          {errorMedios && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700">
              {errorMedios}
            </div>
          )}

          <div className="mt-3 space-y-3">
            {pagos.map((pago, index) => (
              <div
                key={pago.key}
                className="rounded-xl border border-gray-200 p-3"
              >
                <div className="flex items-center gap-2">
                  <select
                    value={pago.medio_pago_id}
                    onChange={(event) =>
                      actualizarPago(
                        pago.key,
                        'medio_pago_id',
                        event.target.value
                      )
                    }
                    disabled={
                      procesando || cargandoMedios
                    }
                    className="h-10 min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 text-sm font-bold text-azul-oscuro outline-none focus:border-verde-esmeralda"
                  >
                    {mediosPago.map((medio) => (
                      <option
                        key={medio.id}
                        value={medio.id}
                      >
                        {medio.nombre}
                      </option>
                    ))}
                  </select>

                  <input
                    ref={index === 0 ? primerMontoRef : null}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={pago.monto}
                    onChange={(event) =>
                      actualizarPago(
                        pago.key,
                        'monto',
                        event.target.value
                      )
                    }
                    disabled={procesando}
                    className="h-10 w-28 rounded-lg border border-gray-200 px-2 text-right text-sm font-bold text-azul-oscuro outline-none focus:border-verde-esmeralda"
                  />

                  {pagos.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        quitarPago(pago.key)
                      }
                      disabled={procesando}
                      className="flex h-10 w-10 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-40"
                      title={`Quitar pago ${index + 1}`}
                    >
                      <Trash2 size={17} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">
                Importe ingresado
              </span>

              <strong className="text-azul-oscuro">
                {money(totalIngresado)}
              </strong>
            </div>

            <div className="mt-1 flex justify-between">
              <span className="text-gray-500">
                Queda pendiente
              </span>

              <strong
                className={
                  totalIngresado > saldo + 0.01
                    ? 'text-red-600'
                    : 'text-verde-esmeralda'
                }
              >
                {totalIngresado > saldo + 0.01
                  ? `Excede ${money(
                      totalIngresado - saldo
                    )}`
                  : money(restante)}
              </strong>
            </div>
          </div>

          {error && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={imprimirPrecuenta}
            className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-azul-oscuro px-5 py-3 font-extrabold text-azul-oscuro transition hover:bg-slate-50"
          >
            <Printer size={19} />
            Imprimir precuenta
          </button>

          <button
            type="button"
            onClick={cobrar}
            disabled={
              procesando ||
              cargandoMedios ||
              !mediosPago.length ||
              !pagos.length ||
              totalIngresado <= 0 ||
              totalIngresado > saldo + 0.01
            }
            className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-verde-esmeralda px-5 py-3 font-extrabold text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CreditCard size={19} />

            {procesando
              ? 'Procesando...'
              : totalIngresado < saldo
                ? `Registrar ${money(
                    totalIngresado
                  )}`
                : `Cobrar ${money(saldo)}`}
          </button>

          <p className="mt-2 text-center text-xs text-gray-400">
            Se solicitará el PIN del operador
          </p>
        </>
      )}
    </section>
  );
}
